import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import Reveal from "../components/Reveal";
import SEO from "../components/SEO";

/* ───── Données du carnet ───── */
const ENTRIES = [
  {
    id: "img-fleuve",
    type: "image",
    title: "L'eau, avant le tissu",
    image: "/heritage/pulaar-argentique-fleuve-senegal-bateau.jpg",
    ratio: "3/4",
    credit: "Argentique · Fleuve Sénégal",
    source: "https://instagram.com/pulaar.co",
    note: "Une image gardée près de l'atelier — pour le rythme et la patience.",
    span: "lg:col-span-5 lg:row-span-2",
  },
  {
    id: "note-geste",
    type: "note",
    quote: "Le geste précède la pensée.",
    attribution: "Note d'atelier — Maabube",
    span: "lg:col-span-4",
  },
  {
    id: "addr-bouclard",
    type: "address",
    name: "Le Bouclard",
    kind: "Bistrot",
    city: "Paris 18ᵉ",
    note: "Pour le bouillon, la lumière des banquettes en cuir et le silence accepté.",
    link: "https://maps.google.com/?q=Le+Bouclard+Paris+18",
    span: "lg:col-span-3",
  },
  {
    id: "video-touki",
    type: "video",
    title: "Touki Bouki",
    author: "Djibril Diop Mambéty · 1973",
    youtubeId: "PCMyq--YH-w",
    poster: "/heritage/dammeel/pulaar-dammeel-gallery-04.jpg",
    note: "Un voyage en boucle, qui n'arrive jamais — ou alors trop tard.",
    span: "lg:col-span-7",
  },
  {
    id: "img-cases",
    type: "image",
    title: "Géométrie peule",
    image: "/heritage/pulaar-cases-peules-architecture-vernaculaire.jpg",
    ratio: "4/5",
    credit: "Archive Pulaar",
    source: "https://instagram.com/pulaar.co",
    note: "L'angle d'un toit suffit parfois à corriger une coupe.",
    span: "lg:col-span-5",
  },
  {
    id: "expo-anatsui",
    type: "exposition",
    title: "Les murs qui dansent",
    artist: "El Anatsui",
    venue: "Centre Pompidou-Metz",
    city: "Metz",
    dates: "07 Sept 2026 — 12 Janv 2027",
    note: "Une exposition à traverser lentement — le métal devient étoffe.",
    link: "https://www.centrepompidou-metz.fr/",
    image: "/heritage/maabube/pulaar-maabube-gallery-03.jpg",
    span: "lg:col-span-7",
  },
  {
    id: "note-etoffe",
    type: "note",
    quote: "Une étoffe se choisit comme une voix : à l'oreille, pas au regard.",
    attribution: "Carnet — Dammeel",
    span: "lg:col-span-5",
  },
  {
    id: "playlist-atelier",
    type: "sound",
    title: "Atelier · Saison 01",
    author: "Pulaar — Curaté",
    cover: "/heritage/pulaar-transmission-artisanat-textile.jpg",
    tracks: [
      "Ali Farka Touré — Soukora",
      "Tinariwen — Sastanàqqàm",
      "Oumou Sangaré — Saa Magni",
      "Bombino — Tehigren",
    ],
    note: "Un son pour couper, assembler, marcher.",
    link: "https://open.spotify.com/search/pulaar",
    span: "lg:col-span-7",
  },
  {
    id: "addr-magnin",
    type: "address",
    name: "Galerie Magnin-A",
    kind: "Galerie · Photographie africaine",
    city: "Paris 11ᵉ",
    note: "Une adresse pour la matière, l'accueil et la lumière du Sud.",
    link: "https://www.magnin-a.com/",
    span: "lg:col-span-5",
  },
  {
    id: "expo-ringgold",
    type: "exposition",
    title: "Black is Beautiful",
    artist: "Faith Ringgold",
    venue: "Musée Picasso",
    city: "Paris",
    dates: "Mars — Sept 2026",
    note: "Le quilt comme manifeste — l'étoffe parle plus fort que le slogan.",
    link: "https://www.museepicassoparis.fr/",
    image: "/heritage/ceddo/pulaar-ceddo-gallery-04.jpg",
    span: "lg:col-span-7",
  },
  {
    id: "video-wenders",
    type: "video",
    title: "Notebook on Cities and Clothes",
    author: "Wim Wenders · Yohji Yamamoto · 1989",
    youtubeId: "0bF6Lz2X9oE",
    poster: "/heritage/ceddo/pulaar-ceddo-gallery-02.jpg",
    note: "« Travailler à l'envers, pour rejoindre quelque chose de vrai. »",
    span: "lg:col-span-5",
  },
  {
    id: "fragment-coupe",
    type: "fragment",
    text: "Un atelier n'est pas un lieu. C'est une intention tenue dans la durée — du fil tendu, des silences, et quelques amis qui repassent toujours.",
    span: "lg:col-span-12",
  },
];

const FILTERS = [
  { id: "all", label: "Tous", match: () => true },
  { id: "image", label: "Images", match: (e) => e.type === "image" },
  { id: "note", label: "Notes", match: (e) => e.type === "note" || e.type === "fragment" },
  { id: "video", label: "Vidéos", match: (e) => e.type === "video" },
  { id: "exposition", label: "À voir", match: (e) => e.type === "exposition" },
  { id: "address", label: "Adresses", match: (e) => e.type === "address" },
  { id: "sound", label: "Sons", match: (e) => e.type === "sound" },
];

/* ───── Cartes par type ───── */

function ImageCard({ entry }) {
  return (
    <a
      href={entry.source}
      target="_blank"
      rel="noreferrer"
      data-testid={`journal-card-${entry.id}`}
      className="group block relative overflow-hidden bg-[#EFE8DA]"
    >
      <div className="overflow-hidden" style={{ aspectRatio: entry.ratio || "3/4" }}>
        <img
          src={entry.image}
          alt={entry.title}
          loading="lazy"
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
          className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/55 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="absolute left-4 right-4 bottom-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-[#FBF8EF]">
        <p className="text-[10px] tracking-[0.28em] uppercase mb-1">{entry.credit}</p>
        <p className="font-serif text-lg leading-tight">{entry.title}</p>
        {entry.note && <p className="mt-1 text-[11px] leading-relaxed text-[#FBF8EF]/85 italic">{entry.note}</p>}
      </div>
      <p className="mt-3 text-[10px] tracking-[0.28em] uppercase text-[#5C4033]">— {entry.title}</p>
    </a>
  );
}

function NoteCard({ entry }) {
  return (
    <div
      data-testid={`journal-card-${entry.id}`}
      className="h-full flex flex-col justify-center border-t border-[#D8D2C5] pt-6"
    >
      <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-6">— Note</p>
      <blockquote className="font-serif italic font-light text-[#121212] text-2xl md:text-3xl leading-[1.25] tracking-[-0.01em]">
        « {entry.quote} »
      </blockquote>
      <p className="mt-6 text-[10px] tracking-[0.28em] uppercase text-[#4A4641]">
        {entry.attribution}
      </p>
    </div>
  );
}

function FragmentCard({ entry }) {
  return (
    <div
      data-testid={`journal-card-${entry.id}`}
      className="py-8 md:py-12 text-center max-w-3xl mx-auto"
    >
      <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-6">— Fragment</p>
      <p className="font-serif italic font-light text-[#121212] text-xl md:text-2xl leading-[1.6]">
        {entry.text}
      </p>
    </div>
  );
}

function AddressCard({ entry }) {
  return (
    <a
      href={entry.link}
      target="_blank"
      rel="noreferrer"
      data-testid={`journal-card-${entry.id}`}
      className="group block bg-[#EFE8DA] p-6 md:p-8 transition-colors duration-500 hover:bg-[#E8DFCD]"
    >
      <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-5">— Adresse</p>
      <h3 className="font-serif text-3xl md:text-4xl tracking-[-0.01em] leading-tight text-[#121212]">
        {entry.name}
      </h3>
      <p className="mt-2 text-[11px] tracking-[0.28em] uppercase text-[#4A4641]">
        {entry.kind} · {entry.city}
      </p>
      <p className="mt-5 text-sm leading-[1.7] text-[#4A4641] italic font-serif">
        {entry.note}
      </p>
      <div className="mt-6 inline-flex items-center gap-1 text-[10px] tracking-[0.28em] uppercase text-[#121212]">
        <span className="link-underline">Voir</span>
        <ArrowUpRight size={12} strokeWidth={1.2} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </a>
  );
}

function VideoCard({ entry }) {
  const [playing, setPlaying] = useState(false);
  const fallbackThumb = `https://img.youtube.com/vi/${entry.youtubeId}/hqdefault.jpg`;
  const thumb = entry.poster || fallbackThumb;
  const embed = `https://www.youtube.com/embed/${entry.youtubeId}?autoplay=1&rel=0`;

  return (
    <div data-testid={`journal-card-${entry.id}`}>
      <div className="relative overflow-hidden bg-[#1A1714] aspect-video">
        {playing ? (
          <iframe
            src={embed}
            title={entry.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="w-full h-full"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="group block w-full h-full relative"
            aria-label={`Lire : ${entry.title}`}
          >
            <img
              src={thumb}
              alt={entry.title}
              loading="lazy"
              draggable="false"
              className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.02]"
              onError={(e) => {
                if (e.currentTarget.src !== fallbackThumb) e.currentTarget.src = fallbackThumb;
              }}
            />
            <div className="absolute inset-0 bg-black/25 group-hover:bg-black/15 transition-colors duration-500" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#FBF8EF]/95 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                <Play size={22} strokeWidth={1.2} className="text-[#121212] ml-1" />
              </span>
            </div>
          </button>
        )}
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-4">
        <div>
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033] mb-1.5">— À regarder</p>
          <p className="font-serif text-lg md:text-xl text-[#121212] leading-tight">{entry.title}</p>
          <p className="mt-1 text-[11px] tracking-[0.2em] uppercase text-[#4A4641]">{entry.author}</p>
        </div>
      </div>
      {entry.note && (
        <p className="mt-3 text-sm font-serif italic text-[#4A4641] leading-relaxed">{entry.note}</p>
      )}
    </div>
  );
}

function ExpositionCard({ entry }) {
  return (
    <a
      href={entry.link}
      target="_blank"
      rel="noreferrer"
      data-testid={`journal-card-${entry.id}`}
      className="group block"
    >
      <div className="grid grid-cols-12 gap-5 md:gap-6 items-stretch">
        {entry.image && (
          <div className="col-span-5 overflow-hidden bg-[#EFE8DA]" style={{ aspectRatio: "4/5" }}>
            <img
              src={entry.image}
              alt={entry.title}
              loading="lazy"
              draggable="false"
              className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
            />
          </div>
        )}
        <div className={entry.image ? "col-span-7 flex flex-col justify-center" : "col-span-12"}>
          <p className="text-[10px] tracking-[0.32em] uppercase text-[#5C4033] mb-4">— À voir</p>
          <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl tracking-[-0.01em] leading-[1.05] text-[#121212]">
            {entry.title}
          </h3>
          <p className="mt-2 font-serif italic text-[#4A4641] text-base md:text-lg">
            {entry.artist}
          </p>
          <p className="mt-4 text-[10px] tracking-[0.32em] uppercase text-[#4A4641]">
            {entry.venue} · {entry.city}
          </p>
          <p className="mt-1 text-[10px] tracking-[0.32em] uppercase text-[#5C4033]">
            {entry.dates}
          </p>
          {entry.note && (
            <p className="mt-4 text-sm font-serif italic text-[#4A4641] leading-relaxed max-w-md">
              {entry.note}
            </p>
          )}
          <div className="mt-5 inline-flex items-center gap-1 text-[10px] tracking-[0.28em] uppercase text-[#121212]">
            <span className="link-underline">Détails</span>
            <ArrowUpRight size={12} strokeWidth={1.2} className="transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </div>
        </div>
      </div>
    </a>
  );
}

function SoundCard({ entry }) {
  return (
    <a
      href={entry.link}
      target="_blank"
      rel="noreferrer"
      data-testid={`journal-card-${entry.id}`}
      className="group block"
    >
      <div className="flex gap-5 md:gap-6 bg-[#1A1714] p-5 md:p-7">
        {/* Cover */}
        <div className="shrink-0 w-28 h-28 md:w-36 md:h-36 overflow-hidden bg-[#3A322C]">
          {entry.cover ? (
            <img
              src={entry.cover}
              alt={entry.title}
              loading="lazy"
              draggable="false"
              className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="font-serif italic text-[#FBF8EF]/40 text-5xl">♪</span>
            </div>
          )}
        </div>
        {/* Tracks */}
        <div className="flex-1 min-w-0 flex flex-col justify-between text-[#FBF8EF]">
          <div>
            <p className="text-[9px] tracking-[0.32em] uppercase text-[#FBF8EF]/55 mb-2">Playlist · Spotify</p>
            <p className="font-serif text-lg md:text-2xl leading-tight mb-3">{entry.title}</p>
            <ul className="text-[11px] md:text-xs text-[#FBF8EF]/70 leading-[1.9] tabular-nums">
              {(entry.tracks || []).slice(0, 4).map((t, i) => (
                <li key={i} className="flex items-baseline gap-3">
                  <span className="text-[#FBF8EF]/35 w-4 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="truncate">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#FBF8EF]/90">
            <span className="w-7 h-7 rounded-full bg-[#1DB954] flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
              <Play size={11} strokeWidth={1.5} className="text-[#1A1714] ml-0.5" />
            </span>
            <span className="link-underline">Écouter sur Spotify</span>
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-4">
        <p className="text-[10px] tracking-[0.28em] uppercase text-[#5C4033]">— À écouter</p>
        <p className="text-[10px] tracking-[0.2em] uppercase text-[#4A4641]">{entry.author}</p>
      </div>
      {entry.note && (
        <p className="mt-3 text-sm font-serif italic text-[#4A4641] leading-relaxed">{entry.note}</p>
      )}
    </a>
  );
}

/* ───── Dispatcher ───── */
function EntryCard({ entry }) {
  switch (entry.type) {
    case "image":
      return <ImageCard entry={entry} />;
    case "note":
      return <NoteCard entry={entry} />;
    case "fragment":
      return <FragmentCard entry={entry} />;
    case "address":
      return <AddressCard entry={entry} />;
    case "video":
      return <VideoCard entry={entry} />;
    case "exposition":
      return <ExpositionCard entry={entry} />;
    case "sound":
      return <SoundCard entry={entry} />;
    default:
      return null;
  }
}

/* ───── Page Journal ───── */
export default function Journal() {
  const [filter, setFilter] = useState("all");

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.id === filter) || FILTERS[0];
    return ENTRIES.filter(f.match);
  }, [filter]);

  return (
    <main data-testid="journal-page" className="relative bg-[#FBF8EF] min-h-screen">
      <SEO
        title="Journal — Carnet de l'atelier"
        description="Un carnet de références, d'adresses, d'images et de gestes. Ce que Pulaar regarde, écoute, traverse et recommande."
        path="/journal"
      />

      {/* Intro */}
      <section className="pt-32 md:pt-40 pb-12 md:pb-16">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <Reveal>
              <p className="col-span-12 lg:col-span-3 text-[10px] tracking-[0.32em] uppercase text-[#5C4033] lg:pt-6">
                — Carnet
              </p>
            </Reveal>
            <div className="col-span-12 lg:col-span-9 lg:col-start-4">
              <Reveal>
                <h1
                  data-testid="journal-title"
                  className="font-serif text-[64px] sm:text-[88px] md:text-[120px] lg:text-[160px] leading-[0.9] tracking-[-0.03em] text-[#121212]"
                >
                  Journal<span className="italic font-light">.</span>
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p
                  data-testid="journal-intro"
                  className="mt-8 md:mt-10 max-w-2xl font-serif italic font-light text-lg md:text-xl leading-[1.65] text-[#4A4641]"
                >
                  Un carnet de références, d&apos;adresses, d&apos;images et de gestes. Ce
                  que Pulaar regarde, écoute, traverse et recommande.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Filtres */}
      <section className="pb-12 md:pb-16">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
          <div
            data-testid="journal-filters"
            className="flex flex-wrap items-center gap-x-7 gap-y-3 border-t border-b border-[#D8D2C5] py-4"
          >
            <span className="text-[10px] tracking-[0.32em] uppercase text-[#4A4641] mr-2">
              Mêler
            </span>
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`journal-filter-${f.id}`}
                className={`text-[11px] tracking-[0.28em] uppercase transition-colors duration-300 ${
                  filter === f.id
                    ? "text-[#121212]"
                    : "text-[#8C8178] hover:text-[#5C4033]"
                }`}
                style={{
                  fontStyle: filter === f.id ? "normal" : "normal",
                  textDecoration: filter === f.id ? "underline" : "none",
                  textUnderlineOffset: "6px",
                  textDecorationThickness: "1px",
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grille éditoriale asymétrique */}
      <section className="pb-32 md:pb-44">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
          <div
            data-testid="journal-grid"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-y-16 md:gap-y-24 gap-x-6 md:gap-x-10"
          >
            {filtered.map((entry, i) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className={`col-span-1 md:col-span-2 ${entry.span || "lg:col-span-6"}`}
                style={{
                  marginTop: i > 0 && i % 5 === 0 ? "2rem" : undefined,
                }}
              >
                <EntryCard entry={entry} />
              </motion.div>
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="text-center font-serif italic text-[#4A4641] py-16">
              Rien dans cette page du carnet pour le moment.
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
